import React, { useEffect, useState } from 'react';
import { Page, Card, Layout, Text, BlockStack, InlineGrid, Spinner } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';

interface AdminStats {
  totalShops: number;
  activeShops: number;
  totalRevenue: number;
  monthlyRevenue: number;
  freePlans: number;
  paidPlans: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats>({
    totalShops: 0,
    activeShops: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    freePlans: 0,
    paidPlans: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const token = localStorage.getItem('admin_token');

      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/admin-stats`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`, // Requerido pelo Supabase para acessar a função
          'X-Admin-Token': token, // Token customizado para validar admin
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="Admin Dashboard"
      primaryAction={{
        content: 'Gerenciar Planos',
        onAction: () => navigate('/admin/plans'),
      }}
      secondaryActions={[
        {
          content: 'Ver Lojas',
          onAction: () => navigate('/admin/shops'),
        },
        {
          content: 'Tickets de Suporte',
          onAction: () => navigate('/admin/support'),
        },
        {
          content: 'Sair',
          onAction: () => {
            localStorage.removeItem('admin_token');
            navigate('/admin/login');
          },
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 3 }} gap="400">
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">
                  Total de Lojas
                </Text>
                <Text as="h2" variant="heading2xl">
                  {stats.totalShops}
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">
                  Lojas Ativas
                </Text>
                <Text as="h2" variant="heading2xl">
                  {stats.activeShops}
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">
                  Receita Mensal (MRR)
                </Text>
                <Text as="h2" variant="heading2xl">
                  ${stats.monthlyRevenue.toFixed(2)}
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">
                  Planos FREE
                </Text>
                <Text as="h2" variant="heading2xl">
                  {stats.freePlans}
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">
                  Planos PRO
                </Text>
                <Text as="h2" variant="heading2xl">
                  {stats.paidPlans}
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">
                  Receita Total
                </Text>
                <Text as="h2" variant="heading2xl">
                  ${stats.totalRevenue.toFixed(2)}
                </Text>
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default AdminDashboard;

